/**
 * Auth API (api-core) — reemplaza `supabase.auth.*`.
 *
 * Flujo JWT propio: Core es el IdP de clientes. El access token (15m) se
 * adjunta a cada request; el refresh token (7d) rota vía /auth/refresh.
 * La sesión se persiste en el `tokenStore`.
 */

import { API_CORE_URL } from './config';
import { apiRequest } from './http';
import {
  clearSession,
  getRefreshToken,
  setSession,
  type AuthSession,
  type SessionUser,
} from './tokenStore';
import type { TokenPair } from './types';

interface RegisterResult {
  user: SessionUser;
  tokens: TokenPair;
}

interface ResetInitiateResult {
  message?: string;
  /** Sólo presente en dev cuando el backend expone el token de prueba. */
  token?: string | null;
}

/** Login de cliente. Persiste la sesión y devuelve el usuario. */
export async function login(email: string, password: string): Promise<SessionUser> {
  const result = await apiRequest<RegisterResult>(API_CORE_URL, '/auth/login', {
    method: 'POST',
    auth: false,
    body: { email, password },
  });
  const session: AuthSession = { user: result.user, tokens: result.tokens };
  setSession(session);
  return result.user;
}

/** Datos de perfil que se recogen al registrarse. */
export interface RegisterProfile {
  name?: string;
  phone?: string;
  /** `YYYY-MM-DD`. Va por `PATCH /profile/me`, no por el registro — ver abajo. */
  birthDate?: string;
  /** `CC` · `CE` · `PA` · `NIT`. Sí lo acepta el registro (`≤ 8`). */
  documentType?: string;
  /** Número de documento. Sí lo acepta el registro (`≤ 64`). */
  documentNumber?: string;
}

/**
 * Registro de cliente. Persiste la sesión (registro devuelve sesión viva).
 *
 * 🔴 **La fecha de nacimiento NO viaja en el registro, y no es un olvido.**
 * Medido sobre el OpenAPI de api-core (2026-09-02): `POST /auth/register`
 * acepta `name` y `phone`, pero **no tiene campo de nacimiento**. La columna
 * `user_profiles.birth_date` **sí existe**, y el único endpoint que la escribe
 * es `PATCH /v1/profile/me`.
 *
 * Por eso el registro es **dos llamadas**: se crea la cuenta —que devuelve
 * sesión viva— y con esa sesión se completa el perfil. Mandarla en el registro
 * la haría **desaparecer en silencio**: api-core ignora los campos que no
 * declara, así que el fan la escribiría y no se guardaría en ningún lado.
 *
 * ⚠️ **Si la segunda llamada falla, la cuenta YA existe.** Se avisa al llamador
 * en vez de tragarlo, pero **no se revierte**: dejar al fan sin cuenta por no
 * haber podido guardar una fecha sería peor que una fecha faltante que después
 * se completa desde el perfil.
 */
export async function register(
  email: string,
  password: string,
  profile: RegisterProfile = {},
): Promise<SessionUser> {
  const result = await apiRequest<RegisterResult>(API_CORE_URL, '/auth/register', {
    method: 'POST',
    auth: false,
    body: {
      email,
      password,
      // Sólo se mandan si vienen: el contrato los declara anulables, y mandar
      // cadenas vacías escribiría un perfil peor que uno sin dato.
      ...(profile.name ? { name: profile.name } : {}),
      ...(profile.phone ? { phone: profile.phone } : {}),
      // El documento SÍ viaja en el registro: a diferencia del nacimiento,
      // `RegisterIn` lo declara. Verificado sobre el OpenAPI de api-core.
      ...(profile.documentType ? { document_type: profile.documentType } : {}),
      ...(profile.documentNumber ? { document_number: profile.documentNumber } : {}),
    },
  });
  const session: AuthSession = { user: result.user, tokens: result.tokens };
  setSession(session);

  if (profile.birthDate) {
    // La sesión ya está persistida, así que este PATCH sale autenticado.
    await updateMyProfile({ birth_date: profile.birthDate });
  }
  return result.user;
}

/** Campos de perfil que acepta `PATCH /v1/profile/me`. */
export interface ProfileUpdate {
  name?: string;
  phone?: string;
  birth_date?: string;
}

/** Actualiza el perfil del usuario autenticado. */
export async function updateMyProfile(patch: ProfileUpdate): Promise<void> {
  await apiRequest<unknown>(API_CORE_URL, '/profile/me', {
    method: 'PATCH',
    body: patch,
  });
}

/** Logout — revoca el refresh token server-side y limpia la sesión local. */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await apiRequest<unknown>(API_CORE_URL, '/auth/logout', {
        method: 'POST',
        body: { refresh_token: refreshToken },
      });
    } catch {
      /* el server puede rechazar un token ya expirado — limpiamos igual */
    }
  }
  clearSession();
}

/** Usuario autenticado actual desde el server (valida el token). */
export async function fetchMe(): Promise<SessionUser> {
  return apiRequest<SessionUser>(API_CORE_URL, '/auth/me');
}

/** Inicia el flujo de reset de contraseña por email. */
export async function initiatePasswordReset(email: string): Promise<ResetInitiateResult> {
  return apiRequest<ResetInitiateResult>(API_CORE_URL, '/auth/password-reset/initiate', {
    method: 'POST',
    auth: false,
    body: { email },
  });
}

/** Consume el token de reset y define la contraseña nueva. */
export async function consumePasswordReset(token: string, newPassword: string): Promise<void> {
  await apiRequest<unknown>(API_CORE_URL, '/auth/password-reset/consume', {
    method: 'POST',
    auth: false,
    body: { token, new_password: newPassword },
  });
}
